import { Link } from 'react-router-dom'
import '../styles/App.css'

function Privacy() {
  return (
    <div className="legal-page">
      <nav className="legal-nav">
        <Link to="/" className="legal-back">Stream Terrarium</Link>
      </nav>

      <div className="legal-content">
        <h1>Privacy Policy</h1>
        <p className="legal-updated">Last updated: April 9, 2026</p>

        <h2>1. Overview</h2>
        <p>
          Stream Terrarium ("we", "our", "the Service") respects your privacy. This policy explains what data we collect,
          how we use it, and your rights regarding that data.
        </p>

        <h2>2. Data We Collect</h2>
        <h3>2.1 Twitch Account Data</h3>
        <p>When you authenticate with Twitch, we receive and store:</p>
        <ul>
          <li><strong>Twitch User ID</strong> — to identify your terrarium entities</li>
          <li><strong>Twitch Username / Display Name</strong> — shown above your plant or insect in the overlay</li>
          <li><strong>OAuth Access Token</strong> — to read chat messages and send responses (stored server-side, never exposed to clients)</li>
        </ul>
        <p>We do <strong>not</strong> collect or store your email address, stream key, password, or payment information.</p>

        <h3>2.2 Terrarium Data</h3>
        <p>We store the following in our PostgreSQL database, associated with your Twitch User ID:</p>
        <ul>
          <li>Plant type, growth stage, health, and position</li>
          <li>Insect type and position</li>
          <li>Cosmetic unlocks and variant selections</li>
          <li>Cumulative Bits used (for perk thresholds)</li>
          <li>Bits transaction history (transaction IDs, SKUs, amounts)</li>
          <li>Custom plant names (if applicable)</li>
        </ul>

        <h3>2.3 Twitch Extension Data</h3>
        <p>
          When using the Terrarium Twitch Extension panel, Twitch provides us with your Twitch User ID and channel ID
          via a signed JWT token. We use this to identify your account and process Bits transactions. We do not receive
          your Twitch password or payment details — all Bits transactions are handled by Twitch directly.
        </p>

        <h3>2.4 Data We Do NOT Collect</h3>
        <ul>
          <li>Email addresses</li>
          <li>IP addresses (not logged by our application)</li>
          <li>Payment or billing information (Twitch handles all Bits transactions)</li>
          <li>Stream keys or broadcasting credentials</li>
          <li>Cookies or tracking pixels (we do not use analytics cookies)</li>
          <li>Chat message content (messages are processed in real-time for commands but not stored)</li>
        </ul>

        <h2>3. How We Use Your Data</h2>
        <ul>
          <li><strong>Terrarium functionality</strong> — to display and persist your plants, insects, and cosmetics</li>
          <li><strong>Bits processing</strong> — to verify transactions and unlock cosmetics/effects/perks</li>
          <li><strong>Chat commands</strong> — to respond to !join, !water, !equip and other commands</li>
          <li><strong>Display names</strong> — to show your username above your entity in the overlay</li>
        </ul>
        <p>We do <strong>not</strong> sell, share, or transfer your data to third parties.</p>

        <h2>4. Data Storage & Security</h2>
        <p>
          Your data is stored in a PostgreSQL database on a secured server. OAuth tokens are stored server-side and
          are never exposed to the client-side application. We use HTTPS for all API communication.
        </p>

        <h2>5. Data Retention</h2>
        <p>
          Your terrarium data is retained as long as your entities exist in a broadcaster's terrarium. If you use
          <code> !leave</code> or a moderator removes your entity, your plant/insect data is deleted. Bits transaction
          history and cosmetic unlocks are retained indefinitely to preserve your unlocks.
        </p>
        <p>
          Broadcaster data (OAuth tokens) is retained as long as the broadcaster has an active terrarium. Broadcasters
          can request deletion by contacting us.
        </p>

        <h2>6. Third-Party Services</h2>
        <p>The Service integrates with:</p>
        <ul>
          <li><strong>Twitch API</strong> — for authentication, chat, subscription checks, and Bits transactions (subject to <a href="https://www.twitch.tv/p/en/legal/terms-of-service/" target="_blank" rel="noopener noreferrer">Twitch's Terms of Service</a>)</li>
          <li><strong>Twitch Extension Platform</strong> — for the Bits shop panel (subject to <a href="https://www.twitch.tv/p/en/legal/developer-agreement/" target="_blank" rel="noopener noreferrer">Twitch's Developer Agreement</a>)</li>
        </ul>
        <p>We do not use Google Analytics, Facebook Pixel, or any third-party tracking services.</p>

        <h2>7. Children's Privacy</h2>
        <p>
          The Service is not directed at children under 13. We do not knowingly collect data from children under 13.
          If you believe a child has provided us with personal data, please contact us and we will delete it.
        </p>

        <h2>8. Your Rights</h2>
        <p>You have the right to:</p>
        <ul>
          <li><strong>Access</strong> your data — contact us to request a copy of your stored data</li>
          <li><strong>Delete</strong> your data — use <code>!leave</code> to remove your terrarium entity, or contact us for full account deletion</li>
          <li><strong>Revoke access</strong> — disconnect the Twitch OAuth connection in your <a href="https://www.twitch.tv/settings/connections" target="_blank" rel="noopener noreferrer">Twitch Settings &gt; Connections</a></li>
        </ul>

        <h2>9. Changes to This Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. Changes will be reflected by the "Last updated" date at
          the top. Continued use of the Service after changes constitutes acceptance.
        </p>

        <h2>10. Contact</h2>
        <p>
          For privacy questions or data deletion requests, contact us via the Stream Terrarium GitHub repository or
          through Twitch at <a href="https://twitch.tv/f00d4tehg0dz" target="_blank" rel="noopener noreferrer">twitch.tv/f00d4tehg0dz</a>.
        </p>
      </div>

      <style>{`
        .legal-page {
          min-height: 100vh;
          background: #050a0f;
          color: #e0e0e0;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
        }
        html, body, #root {
          overflow: visible !important;
          overflow-y: auto !important;
          height: auto !important;
          min-height: 100vh;
        }
        .legal-nav {
          padding: 16px 32px;
          border-bottom: 1px solid rgba(255,255,255,0.06);
        }
        .legal-back {
          color: #888;
          text-decoration: none;
          font-size: 14px;
          font-weight: 500;
        }
        .legal-back:hover { color: #fff; }
        .legal-content {
          max-width: 720px;
          margin: 0 auto;
          padding: 48px 24px 80px;
          line-height: 1.7;
        }
        .legal-content h1 {
          font-size: 28px;
          font-weight: 700;
          margin-bottom: 8px;
          color: #fff;
        }
        .legal-updated {
          color: #666;
          font-size: 13px;
          margin-bottom: 32px;
        }
        .legal-content h2 {
          font-size: 18px;
          font-weight: 600;
          margin-top: 32px;
          margin-bottom: 12px;
          color: #efeff1;
        }
        .legal-content h3 {
          font-size: 15px;
          font-weight: 600;
          margin-top: 20px;
          margin-bottom: 8px;
          color: #ccc;
        }
        .legal-content p {
          margin-bottom: 12px;
          color: #bbb;
          font-size: 14px;
        }
        .legal-content ul {
          margin: 8px 0 16px 20px;
          color: #bbb;
          font-size: 14px;
        }
        .legal-content li { margin-bottom: 6px; }
        .legal-content code {
          background: rgba(145,71,255,0.1);
          padding: 1px 5px;
          border-radius: 3px;
          font-size: 13px;
          color: #c4a3ff;
        }
        .legal-content a { color: #9147ff; }
        .legal-content a:hover { text-decoration: underline; }
      `}</style>
    </div>
  )
}

export default Privacy
